import React, { useEffect, useMemo, useState } from "react";
import {
  PAGE_SIZE,
  compareDates,
  formatMoney,
  formatNumber,
  getMonthKey,
  getMonthLabel,
} from "./utils";
import Pagination from "./ui/Pagination";
import "./RecognitionsTab.css";

function Money({ value, accent = false }) {
  return (
    <span className={accent ? "rr-money--accent" : undefined}>
      <span className="rr-money__currency">₸</span>
      {formatNumber(value)}
    </span>
  );
}

function ReadinessBar({ value }) {
  return (
    <div className="rr-readiness" title={`Готовность ${value}%`}>
      <div className="rr-readiness__fill" style={{ width: `${value}%` }} />
      <span className={`rr-readiness__label${value < 40 ? " is-dark" : ""}`}>{value}%</span>
    </div>
  );
}

function SortIcon({ direction }) {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" fill="none" aria-hidden>
      <path d="M5 1L8.5 5H1.5L5 1Z" fill="currentColor" opacity={direction === "desc" ? 0.35 : 1} />
      <path d="M5 13L1.5 9H8.5L5 13Z" fill="currentColor" opacity={direction === "asc" ? 0.35 : 1} />
    </svg>
  );
}

function RecognitionsTab({ config, data, recognitions, selectedId, onSelect }) {
  const { texts } = config;
  const { projectById, contractById, organizations } = data;
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("");
  const [projectId, setProjectId] = useState("");
  const [organization, setOrganization] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [page, setPage] = useState(1);

  const periodOptions = useMemo(
    () =>
      [...new Set(recognitions.map((item) => getMonthKey(item.date)))].sort().reverse(),
    [recognitions]
  );

  const projectOptions = useMemo(
    () =>
      [...new Set(recognitions.map((item) => item.projectId))]
        .map((id) => projectById[id])
        .sort((a, b) => a.name.localeCompare(b.name, "ru")),
    [recognitions, projectById]
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return recognitions
      .filter((item) => {
        if (period && getMonthKey(item.date) !== period) return false;
        if (projectId && item.projectId !== Number(projectId)) return false;
        if (organization && item.organization !== organization) return false;
        if (query) {
          const haystack = `${projectById[item.projectId].name} ${
            contractById[item.contractId].name
          }`.toLowerCase();
          if (!haystack.includes(query)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const diff = compareDates(a.date, b.date) || a.id - b.id;
        return sortDirection === "asc" ? diff : -diff;
      });
  }, [
    recognitions,
    search,
    period,
    projectId,
    organization,
    sortDirection,
    projectById,
    contractById,
  ]);

  useEffect(() => {
    setPage(1);
  }, [search, period, projectId, organization, recognitions]);

  const total = filtered.reduce((sum, item) => sum + item.current, 0);
  const rows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="rr-filters">
        <label className="rr-search">
          <svg className="rr-search__icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4" />
            <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <input
            className="rr-search__input"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Поиск по проекту или договору..."
          />
        </label>

        <select
          className={`rr-select${period ? " is-active" : ""}`}
          value={period}
          onChange={(event) => setPeriod(event.target.value)}
        >
          <option value="">Все периоды</option>
          {periodOptions.map((key) => (
            <option key={key} value={key}>
              {getMonthLabel(key)}
            </option>
          ))}
        </select>

        <select
          className={`rr-select${projectId ? " is-active" : ""}`}
          value={projectId}
          onChange={(event) => setProjectId(event.target.value)}
        >
          <option value="">Все проекты</option>
          {projectOptions.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>

        <select
          className={`rr-select${organization ? " is-active" : ""}`}
          value={organization}
          onChange={(event) => setOrganization(event.target.value)}
        >
          <option value="">Все организации</option>
          {organizations.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>

        <div className="rr-total">
          {texts.totalLabel}: <strong>{formatMoney(total)}</strong>
        </div>
      </div>

      <div className="rr-table-scroll">
        <table className="rr-table rr-recognitions">
          <thead>
            <tr>
              <th>
                <button
                  type="button"
                  className="rr-th-sort is-sorted"
                  onClick={() => setSortDirection((dir) => (dir === "asc" ? "desc" : "asc"))}
                  title={sortDirection === "asc" ? "Сначала старые" : "Сначала новые"}
                >
                  Дата <SortIcon direction={sortDirection} />
                </button>
              </th>
              <th>Проект</th>
              <th>Договор</th>
              <th>% готовности</th>
              <th>
                Сумма договора
                <br />
                без НДС (₸)
              </th>
              <th>
                {texts.noun}
                <br />
                накопительно
              </th>
              <th>
                {texts.noun} за предыдущие
                <br />
                периоды
              </th>
              <th>
                {texts.noun} за отчетный
                <br />
                период
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr
                key={item.id}
                className={`rr-row${item.id === selectedId ? " is-selected" : ""}`}
                onClick={() => onSelect(item)}
              >
                <td className="rr-recognitions__date">{item.date}</td>
                <td className="rr-recognitions__project">{projectById[item.projectId].name}</td>
                <td className="rr-recognitions__contract">
                  Договор {contractById[item.contractId].name}
                </td>
                <td>
                  <ReadinessBar value={item.readiness} />
                </td>
                <td className="rr-money">
                  <Money value={item.contractAmount} />
                </td>
                <td className="rr-money">
                  <Money value={item.cumulative} />
                </td>
                <td className="rr-money">
                  <Money value={item.previous} />
                </td>
                <td className="rr-money">
                  <Money value={item.current} accent />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="rr-empty-state">По выбранным фильтрам признаний не найдено</div>
        )}
      </div>

      <Pagination page={page} pageSize={PAGE_SIZE} total={filtered.length} onChange={setPage} />
    </>
  );
}

export default RecognitionsTab;
